import api from '../config/api';

const adminService = {
  async getStats() {
    try {
      const response = await api.get('/admin/stats');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch admin statistics'
      };
    }
  },

  async getUsers(page = 1, limit = 20, filters = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      const response = await api.get(`/admin/users?${params}`);
      return {
        success: true,
        data: response.data.users,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },

  async getUserById(userId) {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user details'
      };
    }
  },

  async getUserSubscriptions(userId) {
    try {
      const response = await api.get(`/admin/users/${userId}/subscriptions`);
      return {
        success: response.data?.success !== false,
        data: response.data?.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch subscriptions'
      };
    }
  },

  async updateUser(userId, userData) {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update user'
      };
    }
  },

  async deleteUser(userId) {
    try {
      await api.delete(`/admin/users/${userId}`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete user'
      };
    }
  },

  async assignUserPlan(userId, planId) {
    try {
      const response = await api.post(`/admin/users/${userId}/assign-plan`, { planId });
      return {
        success: response.data?.success !== false,
        data: response.data?.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to assign plan'
      };
    }
  },

  async getShops(page = 1, limit = 20, filters = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      const response = await api.get(`/admin/shops?${params}`);
      return {
        success: true,
        data: response.data.shops,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch shops'
      };
    }
  },

  async getShopById(shopId) {
    try {
      const response = await api.get(`/admin/shops/${shopId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch shop details'
      };
    }
  },

  async updateShop(shopId, shopData) {
    try {
      const response = await api.put(`/admin/shops/${shopId}`, shopData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update shop'
      };
    }
  },

  async deleteShop(shopId) {
    try {
      await api.delete(`/admin/shops/${shopId}`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete shop'
      };
    }
  },

  async getPlans() {
    try {
      const response = await api.get('/admin/plans');
      return {
        success: response.data.success,
        data: response.data.data || response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch plans'
      };
    }
  },

  async getPlanById(planId) {
    try {
      const response = await api.get(`/admin/plans/${planId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch plan details'
      };
    }
  },

  async createPlan(planData) {
    try {
      const response = await api.post('/admin/plans', planData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create plan'
      };
    }
  },

  async updatePlan(planId, planData) {
    try {
      const response = await api.put(`/admin/plans/${planId}`, planData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update plan'
      };
    }
  },

  async deletePlan(planId) {
    try {
      await api.delete(`/admin/plans/${planId}`);
      return {
        success: true
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete plan'
      };
    }
  },

  async getOrders(page = 1, limit = 20, filters = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      const response = await api.get(`/admin/orders?${params}`);
      return {
        success: true,
        data: response.data.orders,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch orders'
      };
    }
  },

  async getLogs(page = 1, limit = 50, filters = {}) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters
      });
      
      const response = await api.get(`/admin/logs?${params}`);
      return {
        success: true,
        data: response.data.logs,
        pagination: response.data.pagination
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch system logs'
      };
    }
  },

  async getUserAnalytics(userId) {
    try {
      const response = await api.get(`/admin/users/${userId}/analytics`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch user analytics'
      };
    }
  },

  async getShopAnalytics(shopId) {
    try {
      const response = await api.get(`/admin/shops/${shopId}/analytics`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch shop analytics'
      };
    }
  },

  async getSystemHealth() {
    try {
      const response = await api.get('/admin/health');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch system health'
      };
    }
  },

  // ============================================
  // TESTING METHOD - Remove after testing
  // ============================================
  async updateSubscriptionExpiry(subscriptionId, expiresAt) {
    try {
      const response = await api.put(`/admin/subscriptions/${subscriptionId}/expiry`, {
        expiresAt
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.message || 'Failed to update subscription expiry'
      };
    }
  }
};

export default adminService;
